/** The operator's in-progress selection, and the size fields that go with it. */
import { CentralImagesDocument, readDefaultStyle, readStyleSize, StyleSize, ThumbnailStyle } from "./centralImagesDocument";

/** The picker's value when no style is enforced; `default-style` is absent rather than empty. */
export const NO_STYLE = "none";

export type StyleChoice = ThumbnailStyle | typeof NO_STYLE;

/** The operator's in-progress state. Sizes stay text so a half-typed number is not read as one. */
export interface Draft {
  style: StyleChoice;
  width: string;
  height: string;
}

/**
 * The platform stores a style size as a 32-bit whole number: it quietly drops the fraction of a
 * decimal and rejects a value beyond this bound outright, so either way the size in the document
 * stops being the size in use.
 */
export const MAX_SIZE = 2147483647;

export function formatSizeField(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

/**
 * A blank field means "not measured yet", which `Number("")` would otherwise turn into a recorded
 * zero. A fraction or a value past the platform's range is refused rather than rounded into one,
 * since the operator would otherwise be shown a size the players' lobby never uses.
 *
 * Zero and negatives are recorded as typed: the platform accepts them and reports the style as one
 * it ignores, and this control mirrors that rather than standing in the way of it.
 */
export function parseSizeField(value: string): number | undefined {
  if (value.trim() === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && Math.abs(parsed) <= MAX_SIZE ? parsed : undefined;
}

export function isSizeFieldRefused(value: string): boolean {
  return value.trim() !== "" && parseSizeField(value) === undefined;
}

export function readDraftSize(draft: Draft): StyleSize | undefined {
  const width = parseSizeField(draft.width);
  const height = parseSizeField(draft.height);
  return width === undefined || height === undefined ? undefined : { width, height };
}

export function draftFromDocument(centralImages: CentralImagesDocument | null): Draft {
  const style = readDefaultStyle(centralImages);
  if (style === undefined) {
    return { style: NO_STYLE, width: "", height: "" };
  }
  const size = readStyleSize(centralImages, style);
  return { style, width: formatSizeField(size?.width), height: formatSizeField(size?.height) };
}

export function draftForStyle(centralImages: CentralImagesDocument | null, style: StyleChoice): Draft {
  if (style === NO_STYLE) {
    return { style, width: "", height: "" };
  }
  const size = readStyleSize(centralImages, style);
  return { style, width: formatSizeField(size?.width), height: formatSizeField(size?.height) };
}
